import * as React from 'react';
import { useNavigate } from 'react-router';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CircularProgress from '@mui/material/CircularProgress';
import { requirementsApi } from '../../services/api';
import { usePref } from '../../services/prefs';
import { useProjectMap } from '../../hooks/useProjectMap';
import { useTypeMap } from '../../hooks/useTypeMap';
import useAutoFocus from '../../hooks/useAutoFocus';
import IdChip from '../common/IdChip';
import TypeBadge from '../common/TypeBadge';
import ScopeToggle from '../common/ScopeToggle';
import ItemTooltip from '../itemCard/ItemTooltip';
import { Ic } from './Icons';

// Lazy pagination: load the first two "pages" up front, then fetch further pages by
// offset as the user scrolls near the bottom of the (scrollable) dropdown.
const PAGE_SIZE = 20;
const INITIAL_LIMIT = PAGE_SIZE * 2; // first two pages
// Debounce the dropdown fetch so typing a word is one request, not one per keystroke.
const DEBOUNCE_MS = 280;

/**
 * The global "Search requirements" box (desktop `global-search` / mobile
 * `global-search-mobile`, both render this same component) — GH #201 made the
 * mobile input reachable, but neither input ever showed the user anything: it
 * silently filtered the requirements tree, which is invisible unless you
 * happen to already be looking at the tree. This adds a live results popup so
 * search is visible from anywhere in the app (Dashboard, Item Detail, mobile).
 *
 * GH #285: intentionally decoupled from the tree's own filter. This box is a
 * jump-to (pick a result → open the item); the tree's own filter-popover
 * search (`treeSearch` in RequirementsTreeView) is the only thing that
 * narrows the tree's rows. A prior fix briefly coupled the two via
 * `SearchContext` — reverted per product direction on #285.
 */
export default function GlobalSearchBox({
  testId, placeholder = 'Search requirements', focusOnMount = false, fullWidth = false, showShortcut = false,
}) {
  // Local query state — the top-bar search is a jump-to (click a result → open the item),
  // decoupled from the tree filter (which has its own box) so typing here is ONE request,
  // not a second tree search on top of the dropdown.
  const [query, setQuery] = React.useState('');
  // #793: renamed from `autoFocus` — the caller (AppShell's mobile full-search)
  // mounts this component FRESH each time it's summoned, so this is genuinely
  // focus-on-mount, but naming the prop `autoFocus` itself tripped
  // jsx-a11y/no-autofocus at the CALL site (the rule flags the attribute name
  // on any JSX element, not only DOM ones). See hooks/useAutoFocus.
  const focusRef = useAutoFocus(focusOnMount);
  // Text (keyword) ⟷ Vector (semantic) scope — same toggle as the ⌘K palette.
  const [scope, setScope] = React.useState('text');
  const navigate = useNavigate();
  const showHumanIds = usePref('showHumanIds');
  // Search spans every project the user can see; label each match with its project
  // (only when there's more than one, so single-project users see no clutter).
  const projectMap = useProjectMap();
  const showProject = Object.keys(projectMap).length > 1;
  const typeMap = useTypeMap();

  const anchorRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const slowTimerRef = React.useRef(null);
  const seqRef = React.useRef(0);

  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState('idle'); // idle | loading | slow | ready | error
  const [errorMsg, setErrorMsg] = React.useState(null);
  const [results, setResults] = React.useState([]);
  const [highlight, setHighlight] = React.useState(-1);
  const [anchorWidth, setAnchorWidth] = React.useState(280);
  // Lazy-load pagination state.
  const [offset, setOffset] = React.useState(0);   // next offset to fetch
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const queryRef = React.useRef('');               // current query, for loadMore()

  // Keep the popup's width in sync with the input (mobile is full-width and
  // fluid; desktop is a fixed 280px box) — re-measured whenever it opens.
  React.useLayoutEffect(() => {
    if (anchorRef.current) setAnchorWidth(anchorRef.current.getBoundingClientRect().width);
  }, [open, fullWidth]);
  React.useEffect(() => {
    const measure = () => { if (anchorRef.current) setAnchorWidth(anchorRef.current.getBoundingClientRect().width); };
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  React.useEffect(() => {
    const q = (query || '').trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    if (!q) {
      seqRef.current += 1;
      queryRef.current = '';
      setOpen(false);
      setStatus('idle');
      setResults([]);
      setHighlight(-1);
      setOffset(0);
      setHasMore(false);
      setLoadingMore(false);
      return undefined;
    }
    setOpen(true);
    setStatus('loading');
    setErrorMsg(null);
    queryRef.current = q;
    const mySeq = ++seqRef.current;
    debounceRef.current = setTimeout(() => {
      // #201 follow-up: a cold-starting backend (e.g. a spun-down Render free-tier
      // dyno) can take several seconds to answer the FIRST request — without this,
      // "Searching…" gave no sign anything was still happening, which reads exactly
      // like "no results are ever displayed" if the user gives up watching it.
      slowTimerRef.current = setTimeout(() => {
        if (seqRef.current === mySeq) setStatus('slow');
      }, 4000);
      // Text = fast flat quick-search (id+title, tenant-wide); Vector = semantic search.
      // (Not /items/search — the tree endpoint builds an ancestor tree and, for a broad
      // query across many projects, took >20s and timed out; the dropdown just needs a
      // short jump-to list.)
      // Load the first two pages. Vector (semantic) returns a ranked top-N, so it isn't
      // offset-paginated — load INITIAL_LIMIT and stop.
      const firstPage = scope === 'vector'
        ? requirementsApi.vectorSearch(q, INITIAL_LIMIT)
        : requirementsApi.quickSearch(q, INITIAL_LIMIT, 0);
      firstPage.then((rows) => {
        if (seqRef.current !== mySeq) return; // superseded by a newer keystroke
        clearTimeout(slowTimerRef.current);
        setResults(rows);
        setOffset(rows.length);
        setHasMore(scope !== 'vector' && rows.length >= INITIAL_LIMIT);
        setLoadingMore(false);
        setHighlight(-1);
        setStatus('ready');
      }).catch((err) => {
        if (seqRef.current !== mySeq) return;
        clearTimeout(slowTimerRef.current);
        console.error('Global search failed:', err);
        setResults([]);
        setErrorMsg(err?.code === 'ECONNABORTED'
          ? 'Search timed out — try again in a moment.'
          : 'Search failed — try again.');
        setStatus('error');
      });
    }, DEBOUNCE_MS);
    return () => { clearTimeout(debounceRef.current); clearTimeout(slowTimerRef.current); };
  }, [query, scope]);

  // Select a result: land the user on that item. Reuses the same `#item=<id>`
  // deep-link hash the tree already listens for (RequirementLink / ItemTooltip /
  // ActivityFeed all use it) — hop to the tree+detail view first if we're not
  // already there, then set the hash so the tree opens/selects it in place.
  const handleSelect = React.useCallback((item) => {
    setOpen(false);
    setHighlight(-1);
    setQuery('');
    if (window.location.pathname !== '/') navigate('/');
    window.location.hash = `item=${item.id}`;
  }, [navigate, setQuery]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); }
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < results.length) {
        e.preventDefault();
        handleSelect(results[highlight]);
      }
    }
  };

  // Lazy-load the next page when the user scrolls near the bottom of the dropdown.
  // Guarded by the seq so a query change mid-flight abandons the append. (Text mode only —
  // Vector is a ranked top-N, not offset-paginated.)
  const loadMore = React.useCallback(() => {
    const q = queryRef.current;
    if (!q || !hasMore || loadingMore || scope === 'vector') return;
    const mySeq = seqRef.current;
    setLoadingMore(true);
    requirementsApi.quickSearch(q, PAGE_SIZE, offset).then((rows) => {
      if (seqRef.current !== mySeq) return; // a newer query took over
      setResults((prev) => [...prev, ...rows]);
      setOffset((o) => o + rows.length);
      setHasMore(rows.length >= PAGE_SIZE);
      setLoadingMore(false);
    }).catch(() => { if (seqRef.current === mySeq) setLoadingMore(false); });
  }, [hasMore, loadingMore, offset, scope]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  // Group the flat results by project so the dropdown shows a project heading, then the
  // requirements under it (id + title), instead of a per-row project badge. `results` stays
  // flat for keyboard nav; each row keeps its flat index `i` for highlight/Enter.
  const groups = React.useMemo(() => {
    const order = [];
    const byProj = new Map();
    results.forEach((r, i) => {
      const pid = r.project_id || '__none__';
      if (!byProj.has(pid)) { byProj.set(pid, []); order.push(pid); }
      byProj.get(pid).push({ r, i });
    });
    return order.map((pid) => ({ pid, name: projectMap[pid], rows: byProj.get(pid) }));
  }, [results, projectMap]);
  // Only show project headings when the matches actually span more than one project.
  const showHeadings = showProject && groups.length > 1;

  return (
    <>
      <div
        ref={anchorRef}
        className="search"
        style={fullWidth ? { flex: 1, maxWidth: 'none' } : { maxWidth: 280 }}
      >
        <Ic name="search" sm />
        <input
          data-testid={testId}
          placeholder={placeholder}
          value={query}
          ref={focusRef}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if ((query || '').trim()) setOpen(true); }}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls="global-search-listbox"
        />
        {showShortcut && <span className="kbd">⌘K</span>}
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        // Desktop: the field is narrow, so widen the dropdown well beyond it and anchor by its
        // right edge (bottom-end) so it grows LEFT instead of off the right of the screen —
        // giving room for the project heading + full requirement title. Mobile is full-width.
        placement={fullWidth ? 'bottom-start' : 'bottom-end'}
        style={{ zIndex: 1200, width: fullWidth ? anchorWidth : Math.min(480, Math.max(anchorWidth, 420)) }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ]}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            className="cardp"
            elevation={0}
            // The Popper portals to <body>, OUTSIDE the .app/.theme scope where ds.css
            // defines --border/--e1/--base, so the `cardp` var()-based border+shadow+bg
            // resolve to nothing and the popup rendered borderless/shadowless. Restore the
            // card chrome with theme tokens (these reach portaled content via emotion).
            sx={{
              p: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              boxShadow: 6,
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, padding: '4px 6px 6px', borderBottom: '1px solid var(--border)', marginBottom: 4,
            }}
            >
              <span className="t-over" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                {scope === 'vector' ? 'Semantic' : 'Requirements'}
              </span>
              <ScopeToggle mode={scope} onChange={setScope} testIdPrefix="search-scope" />
            </div>
            {/* Only the results scroll — the Text/Vector header stays pinned. Scrolling near
                the bottom lazy-loads the next page (loadMore). */}
            <div
              className="gsb-scroll"
              data-testid="search-results-scroll"
              onScroll={onScroll}
              style={{ maxHeight: 340, overflowY: 'auto' }}
            >
            {status === 'loading' && (
              <div className="t-over" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                <CircularProgress size={13} thickness={5} />
                Searching…
              </div>
            )}
            {status === 'slow' && (
              <div className="t-over" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                <CircularProgress size={13} thickness={5} />
                Still searching — the server may be waking up, this can take a moment…
              </div>
            )}
            {status === 'error' && (
              <div className="t-over" style={{ padding: '8px 10px' }}>{errorMsg || 'Search failed — try again.'}</div>
            )}
            {status === 'ready' && results.length === 0 && (
              <div className="t-over" style={{ padding: '8px 10px' }}>
                {scope === 'vector'
                  ? 'No semantic matches (the semantic index may still be building — see issue #223).'
                  : <>No matches for &ldquo;{query.trim()}&rdquo;</>}
              </div>
            )}
            {results.length > 0 && (
              <ul
                id="global-search-listbox"
                role="listbox"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                {groups.map((g) => (
                  <React.Fragment key={g.pid}>
                    {showHeadings && (
                      <li
                        role="presentation"
                        data-testid="search-project-heading"
                        className="t-over"
                        style={{
                          padding: '7px 10px 3px', fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 0.3, opacity: 0.75,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {g.name || 'Other'}
                      </li>
                    )}
                    {g.rows.map(({ r, i }) => (
                      <li
                        key={r.id}
                        role="option"
                        aria-selected={i === highlight}
                        className={`snav${i === highlight ? ' active' : ''}`}
                        style={{ height: 'auto', minHeight: 30, gap: 8 }}
                        onMouseEnter={() => setHighlight(i)}
                        onMouseDown={(e) => { e.preventDefault(); handleSelect(r); }}
                      >
                        <ItemTooltip item={{ id: r.id, display_id: r.display_id, title: r.title, type: typeMap[r.type_id] }}>
                          <IdChip id={r.id} displayId={r.display_id} showHumanIds={showHumanIds} disableTooltip />
                        </ItemTooltip>
                        <span style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                        }}
                        >
                          {r.title}
                        </span>
                        <TypeBadge label={typeMap[r.type_id]} />
                      </li>
                    ))}
                  </React.Fragment>
                ))}
              </ul>
            )}
            {loadingMore && (
              <div className="t-over" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                <CircularProgress size={12} thickness={5} />
                Loading more…
              </div>
            )}
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
